import { browserStorage, type StorageKind } from '~/composables/usePersistedPref'
import { SCOPE_STORAGE_KEY } from '#shared/web-chat/scope'
import { ORG_OVERRIDE_STORAGE_KEY } from '#shared/organizations/orgOverride'

/**
 * What the browser remembers about the person who is signed in, and what it forgets when they sign
 * out (MAR-1458).
 *
 * Web storage is per BROWSER, not per account. Before this, signing out cleared the session cookie
 * and Firebase's own persistence and left everything else behind, so the next account to sign in on
 * the same browser inherited it: Alex signed out, created a brand-new free account, and landed on the
 * Slow chat tier the previous account had picked (`marcus-model`). The same leak carried the last
 * account's chat scope (case/client ids), a super-admin's org override, an onboarding basket and the
 * previous person's half-typed landing question.
 *
 * EVERY KEY THE APP STORES IS CLASSIFIED HERE, one way or the other.
 * `tests/unit/perUserClientState.test.ts` sweeps `app/` for storage-key literals and fails on one
 * that is in neither list, and on an entry here that no longer names a key anything uses — so a new
 * preference has to decide which kind it is, and a renamed one cannot leave a dead entry behind.
 *
 * - PER USER: identity- or organization-bound data, or something a person typed. Cleared on sign-out.
 * - PER DEVICE: how this browser is set up (theme, language, layout, cookie consent, a view toggle).
 *   Kept: a person signing out does not take the screen's layout with them.
 *
 * Cookies: the app's own are `__session` and the session hint (both HttpOnly, removed by the server
 * through `clearSessionCookie()`), and `marcus_analytics_consent` (per device — consent is a
 * property of the browser, and asking again on every sign-out would be the wrong answer). Nuxt
 * `useState` is in-memory and dies with the hard navigation every sign-out ends in; the one piece a
 * same-page reader could still see, the chat tier, is reset explicitly by `useAuth().signOut()`.
 */

export interface StoredKey {
  /** The exact key, or — with `prefix` — the start of a family of keys (`marcus:doc:<url>`). */
  key: string
  kind: StorageKind
  prefix?: boolean
}

export const PER_USER_STORAGE_KEYS: readonly StoredKey[] = [
  // The chat tier (`useModelSelector`) — the MAR-1458 bug itself.
  { key: 'marcus-model', kind: 'local' },
  // A super-admin's QRA version toggle (`useQraVersion`).
  { key: 'marcus-qra-version', kind: 'local' },
  // A super-admin's org impersonation (`utils/orgOverride.ts`) — rides on every request.
  { key: ORG_OVERRIDE_STORAGE_KEY, kind: 'local' },
  // The chat scope: a case or client id of the previous account's organization (`useChatScope`).
  { key: SCOPE_STORAGE_KEY, kind: 'local' },
  { key: SCOPE_STORAGE_KEY, kind: 'session' },
  // The document browser's open folder path (`stores/documents.ts`).
  { key: 'marcus:browser-path', kind: 'local' },
  // "This firm has recent activity" (`welcome/RecentActivity.vue`).
  { key: 'marcus.recentActivity.seen', kind: 'local' },
  // A landing question someone typed before signing in (`useLandingQuestion`).
  { key: 'marcus_landing_question', kind: 'local' },
  // Word document URL → Marcus document id (`useWordBridge`).
  { key: 'marcus:doc:', kind: 'local', prefix: true },
  // Per-workflow batch toggle and per-template column choices (workflow ids of one organization).
  { key: 'marcus:batch-on:', kind: 'local', prefix: true },
  { key: 'marcus:batch-columns:', kind: 'local', prefix: true },
  // Onboarding: the plan basket and voucher (`onboarding/PlanStep.vue`), and the name step's draft.
  { key: 'marcus:onboarding-plan-basket', kind: 'session' },
  { key: 'marcus:onboarding:name-draft:', kind: 'session', prefix: true },
]

export const PER_DEVICE_STORAGE_KEYS: readonly string[] = [
  'marcus_color_mode', // theme
  'marcus_locale', // UI language
  'marcus_analytics_consent', // cookie consent (also a cookie of the same name)
  'marcus:panels', // shell layout: drawers, sidebar width, chat corner
  'marcus_settings_nav_collapsed', // settings nav collapsed
  'marcus:browser-sources', // document browser grouping
  'marcus:browser-group-mode', // its pre-#158 spelling, read once for migration
  'marcus:browser-sort', // document browser sort field
  'marcus:browser-order', // document browser sort order
  'marcus:clients-show-suggestions', // clients page suggestions toggle
  'marcus:clients-sort', // clients/cases sort
  'marcus:expedientes-density', // clients/cases list density
  'marcus:documents-view', // documents page list/grid
  'docx.showChanges', // DOCX viewer tracked-changes toggle
  'laws.indexCollapsed', // law page index collapsed
  // An org invite link opened before signing in. It belongs to whoever is ABOUT to sign in, not to
  // the account signing out: someone who opens an invite while signed in as the wrong account signs
  // out to accept it, and clearing it here would drop the invite on the way (`login.vue`, `setup.vue`
  // clear it themselves once it is used).
  'marcus-org-invite-token',
]

function clearFrom(storage: Storage | null, entries: readonly StoredKey[]): void {
  if (!storage) return
  try {
    const prefixes = entries.filter(e => e.prefix).map(e => e.key)
    const exact = new Set(entries.filter(e => !e.prefix).map(e => e.key))
    const doomed: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i)
      if (k !== null && (exact.has(k) || prefixes.some(p => k.startsWith(p)))) doomed.push(k)
    }
    // Collected first: removing while iterating by index would skip the key after each removal.
    for (const k of doomed) storage.removeItem(k)
  } catch {
    // Blocked site data: there is nothing stored to leak either.
  }
}

/** Forget every per-user key in this browser's storage. Per-device keys are left alone. Never throws. */
export function clearPerUserClientState(): void {
  for (const kind of ['local', 'session'] as const) {
    clearFrom(browserStorage(kind), PER_USER_STORAGE_KEYS.filter(e => e.kind === kind))
  }
}
